import type {
  AspectConfiguration,
  ControlConfiguration,
  SpeakerBindingKey,
  SpeakerControlConfiguration,
} from '@src/components/sidepanel/CompositionSettings/types';

/**
 * The Company line of a nametag (PLAT-12130): one rule-based edit that turns
 * a two-line nametag (Name / Title) into three (Name / Position / Company), and
 * its reverse. Set as Graphic Standard's Company Line toggle runs it on the
 * template's own source and controls.
 *
 * First, a spare: an empty text the design already has beside the Title,
 * inside the same plate, with its own text control (dev "Blue White" has an
 * unused "Position" text between Name and Title). It is switched on and bound,
 * and nothing in the source changes; the upper of the two lines shows the
 * Position, the lower the Company.
 *
 * Else the same rules as the template design studio's `addCompanyLine`
 * (arbor-poc-template-design-studio, tds-39, D-23), measured on 74 dev
 * nametags and three renders:
 * - the Title is cloned as "Company" directly below it at the same size; else,
 *   when there is no room and the Title auto-fits, the two share its box;
 * - inside the Title's own container (the canvas or its group), appended last
 *   so no control's elementPaths index moves;
 * - on the track right above the Title, so a plate or wipe drawn over the
 *   Title covers the line too (dev Toro Name Tag's intro box);
 * - every control that styles only the Title (font, colour…) styles it too;
 * - bound to the speaker's company; the Title line becomes the Position line
 *   (PLAT-12179), since the one-line Title already holds the company;
 * - refused, with the reason, when it would leave its container, fall off the
 *   plate behind the Title (dev Blue White), overlap a text, or set a shared
 *   line under MIN_LINE_PX at the nametag's smallest framing.
 */

type El = Record<string, unknown> & { elements?: El[] };
type SourceLike = { height?: unknown; elements?: unknown[] } & Record<
  string,
  unknown
>;

export type CompanyLineResult<S> =
  | { ok: true; source: S; controls: ControlConfiguration[]; note: string }
  | { ok: false; reason: string };

const COMPANY_GAP = 0.15; // of the Title's height, between the two lines
/** The smallest a shared line may be, in px of the 720-high video frame. */
export const MIN_LINE_PX = 14;

const pct = (v: unknown): number | null => {
  if (typeof v !== 'string') return null;
  const m = /^\s*(-?\d+(?:\.\d+)?)\s*%\s*$/.exec(v);
  return m ? Number(m[1]) : null;
};
const fmt = (n: number) => `${Math.round(n * 10000) / 10000}%`;

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

function boxOf(el: El): Box | null {
  const x = pct(el.x);
  const y = pct(el.y);
  const width = pct(el.width);
  const height = pct(el.height);
  if (x === null || y === null || width === null || height === null)
    return null;
  const xa = pct(el.x_anchor) ?? 50;
  const ya = pct(el.y_anchor) ?? 50;
  return {
    left: x - (width * xa) / 100,
    top: y - (height * ya) / 100,
    width,
    height,
  };
}

const overlaps = (a: Box, b: Box) =>
  a.left < b.left + b.width &&
  b.left < a.left + a.width &&
  a.top < b.top + b.height &&
  b.top < a.top + a.height;

const contains = (outer: Box, inner: Box) =>
  inner.left >= outer.left - 0.5 &&
  inner.top >= outer.top - 0.5 &&
  inner.left + inner.width <= outer.left + outer.width + 0.5 &&
  inner.top + inner.height <= outer.top + outer.height + 0.5;

function at(root: { elements?: unknown[] }, path: number[]): El | undefined {
  let node: El | undefined = root as El;
  for (const i of path) node = node?.elements?.[i];
  return node;
}

function scaleOf(config: AspectConfiguration | null | undefined): number {
  const raw = config?.scale;
  if (raw === null || raw === undefined || raw === '') return 1;
  if (typeof raw === 'number') return raw;
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return 1;
  return raw.trim().endsWith('%') ? n / 100 : n;
}

/** The smallest scale a template is framed at across the video aspects. */
export function minFramingScale(
  configs: Array<AspectConfiguration | null | undefined>,
): number {
  const scales = configs
    .filter(Boolean)
    .map(scaleOf)
    .filter(n => n > 0);
  return scales.length ? Math.min(...scales) : 1;
}

function speakerOf(
  controls: ControlConfiguration[],
): SpeakerControlConfiguration | undefined {
  return controls.find(
    (c): c is SpeakerControlConfiguration => c.type === 'speaker',
  );
}

/** True when the template's speaker control already binds a Company line. */
export function hasCompanyLine(controls: ControlConfiguration[]): boolean {
  return Object.values(speakerOf(controls)?.controlBindings ?? {}).includes(
    'organization',
  );
}

const PLACEHOLDER = 'Acme Corp'; // generic, never a real company

export function addCompanyLine<S extends SourceLike>(
  source: S | null | undefined,
  controls: ControlConfiguration[],
  options: { minScale?: number } = {},
): CompanyLineResult<S> {
  const refuse = (reason: string): CompanyLineResult<S> => ({
    ok: false,
    reason,
  });
  if (!source?.elements?.length) return refuse('The nametag has no design.');
  const sp = speakerOf(controls);
  if (!sp) return refuse('The nametag has no speaker control.');
  const bindings = sp.controlBindings ?? {};
  if (Object.values(bindings).includes('organization'))
    return refuse('It already has a Company line.');
  const byId = new Map(controls.filter(c => c.id).map(c => [c.id!, c]));
  const titleEntry = Object.entries(bindings).find(
    ([, f]) => f === 'title' || f === 'roleLine' || f === 'role',
  );
  const titleControl = titleEntry ? byId.get(titleEntry[0]) : undefined;
  if (!titleEntry || !titleControl)
    return refuse('No line is filled with the speaker’s title.');
  if ((titleControl.elementPaths ?? []).length !== 1)
    return refuse('The title control fills more than one element.');
  const titlePath = titleControl.elementPaths[0];
  const parentPath = titlePath.slice(0, -1);
  const titleIndex = titlePath[titlePath.length - 1];
  const container = parentPath.length ? at(source, parentPath) : (source as El);
  const siblings = container?.elements ?? [];
  const where = parentPath.length ? 'its group' : 'the nametag';
  const title = at(source, titlePath);
  if (!title || title.type !== 'text')
    return refuse('The title control does not fill a text.');
  const box = boxOf(title);
  if (!box)
    return refuse(
      'The title is placed in px, so the line cannot be placed by rule.',
    );

  // A spare text the design already has (see the header): empty, unbound,
  // next to the Title (within one Title height), across most of its width,
  // inside the plate behind the Title, and with its own enabled-able text
  // control to bind.
  const boundIds = new Set(Object.keys(bindings));
  const plateBehind = siblings.find((el, i) => {
    if (i === titleIndex || el.type === 'text') return false;
    const behind = boxOf(el);
    return behind ? contains(behind, box) : false;
  });
  const plateBox = plateBehind ? boxOf(plateBehind) : null;
  const spare = siblings
    .map((el, i) => ({ el, i, b: boxOf(el) }))
    .find(({ el, i, b }) => {
      if (i === titleIndex || el.type !== 'text' || !b) return false;
      if (typeof el.text === 'string' && el.text.trim()) return false;
      const path = JSON.stringify([...parentPath, i]);
      const control = controls.find(
        c =>
          c.type === 'text' &&
          c.id &&
          (c.elementPaths ?? []).length === 1 &&
          JSON.stringify(c.elementPaths[0]) === path,
      );
      if (!control || boundIds.has(control.id!)) return false;
      const gap = Math.max(
        b.top - (box.top + box.height),
        box.top - (b.top + b.height),
      );
      const shared =
        Math.min(b.left + b.width, box.left + box.width) -
        Math.max(b.left, box.left);
      return (
        gap <= box.height &&
        shared >= 0.5 * Math.min(b.width, box.width) &&
        (!plateBox || contains(plateBox, b))
      );
    });
  if (spare) {
    const spareControl = controls.find(
      c =>
        c.type === 'text' &&
        JSON.stringify(c.elementPaths?.[0]) ===
          JSON.stringify([...parentPath, spare.i]),
    )!;
    const spareAbove = spare.b!.top < box.top;
    const nb: Record<string, SpeakerBindingKey> = {};
    for (const [id, f] of Object.entries(bindings)) nb[id] = f;
    nb[titleEntry[0]] = spareAbove ? 'organization' : 'role';
    nb[spareControl.id!] = spareAbove ? 'role' : 'organization';
    const spareName = String(spare.el.name ?? 'spare');
    return {
      ok: true,
      source: structuredClone(source),
      controls: controls.map(c => {
        if (c === sp)
          return {
            ...sp,
            controlBindings: nb,
            companyLine: { titleControlId: titleEntry[0] },
          };
        if (c === spareControl) return { ...c, enabled: true };
        return c;
      }),
      note: spareAbove
        ? `Uses the empty "${spareName}" text for the Position line; "${String(title.name ?? 'Title')}" shows the Company.`
        : `Uses the empty "${spareName}" text for the Company line.`,
    };
  }

  const problemFor = (line: Box): string | null => {
    if (line.top + line.height > 100.5)
      return `There is no room under the title inside ${where}.`;
    const plate = siblings.find((el, i) => {
      if (i === titleIndex || el.type === 'text') return false;
      const behind = boxOf(el);
      return behind ? contains(behind, box) && !contains(behind, line) : false;
    });
    if (plate)
      return `The line would fall outside "${String(plate.name ?? 'the plate')}" behind the title.`;
    const clash = siblings.find((el, i) => {
      if (i === titleIndex || el.type !== 'text') return false;
      const other = boxOf(el);
      return other ? overlaps(line, other) : false;
    });
    if (clash)
      return `The line would overlap "${String(clash.name ?? 'a text')}".`;
    return null;
  };

  const shift = box.height * (1 + COMPANY_GAP);
  const below: Box = { ...box, top: box.top + shift };
  const belowProblem = problemFor(below);
  const autoFits = title.font_size === undefined || title.font_size === null;
  const half = (box.height * (1 - COMPANY_GAP)) / 2;
  const split: Box = {
    ...box,
    top: box.top + box.height - half,
    height: half,
  };
  let containerPx = Number(source.height) || 0;
  for (let depth = 1; depth <= parentPath.length; depth++) {
    const h = pct(at(source, parentPath.slice(0, depth))?.height);
    if (h !== null) containerPx *= h / 100;
  }
  const splitPx = (half / 100) * containerPx * (options.minScale ?? 1);
  let method: 'below' | 'split';
  if (!belowProblem) method = 'below';
  else if (autoFits && splitPx >= MIN_LINE_PX && !problemFor(split))
    method = 'split';
  else
    return refuse(
      `${belowProblem} ${
        !autoFits
          ? 'Its title has a fixed font size, so it cannot share its box.'
          : splitPx < MIN_LINE_PX
            ? `Sharing the title’s box would set each line about ${Math.round(splitPx)} px tall in the video, too small to read.`
            : ''
      }`.trim(),
    );

  const next: S = structuredClone(source);
  const nextContainer = parentPath.length
    ? at(next, parentPath)!
    : (next as unknown as El);
  const nextTitle = at(next, titlePath)!;
  const company: El = structuredClone(title);
  delete company.id;
  company.name = 'Company';
  company.text = PLACEHOLDER;
  const ya = pct(title.y_anchor) ?? 50;
  const yFor = (b: Box) => fmt(b.top + (b.height * ya) / 100);
  if (method === 'below') {
    company.y = fmt((pct(title.y) ?? 0) + shift);
  } else {
    company.y = yFor(split);
    company.height = fmt(half);
    nextTitle.y = yFor({ ...box, height: half });
    nextTitle.height = fmt(half);
  }
  const titleTrack = Number(title.track) || 0;
  const elements = nextContainer.elements ?? [];
  if (titleTrack)
    for (const el of elements)
      if ((Number(el.track) || 0) > titleTrack) el.track = Number(el.track) + 1;
  company.track = titleTrack
    ? titleTrack + 1
    : elements.reduce((m, el) => Math.max(m, Number(el.track) || 0), 0) + 1;
  nextContainer.elements = [...elements, company];
  const companyIndex = siblings.length;
  const companyPath = [...parentPath, companyIndex];
  const titleKey = JSON.stringify(titlePath);
  const companyId = `control_text_text_Text Company_${companyIndex}`;

  const out: ControlConfiguration[] = [];
  for (const c of controls) {
    if (c === sp) {
      const nb: Record<string, SpeakerBindingKey> = {};
      for (const [id, f] of Object.entries(bindings))
        nb[id] = id === titleEntry[0] ? 'role' : f;
      nb[companyId] = 'organization';
      out.push({
        ...sp,
        controlBindings: nb,
        companyLine: { titleControlId: titleEntry[0] },
      });
      continue;
    }
    const stylesTitle =
      c !== titleControl &&
      c.type !== 'text' &&
      (c.elementPaths ?? []).some(p => JSON.stringify(p) === titleKey);
    out.push(
      stylesTitle
        ? { ...c, elementPaths: [...c.elementPaths, companyPath] }
        : c,
    );
    if (c === titleControl) {
      const copy = {
        ...structuredClone(titleControl),
        label: 'Company',
        id: companyId,
        elementPaths: [companyPath],
        default: { value: PLACEHOLDER },
      } as ControlConfiguration & {
        customValue?: unknown;
        customLabel?: unknown;
        translations?: unknown;
      };
      delete copy.customValue;
      delete copy.customLabel;
      delete copy.translations;
      out.push(copy);
    }
  }

  return {
    ok: true,
    source: next,
    controls: out,
    note:
      method === 'below'
        ? 'Adds a Company line under the title, the same size.'
        : 'Adds a Company line by sharing the title’s box: both lines set smaller.',
  };
}

/** The on-canvas placement a nametag keeps when it is re-created. */
const PLACEMENT_KEYS = ['x', 'y', 'width', 'height'] as const;

/**
 * What a nametag re-created from its saved template takes over from the copy
 * it replaces (PLAT-12130): its placement on the canvas, and every control
 * value the creator gave it that the save left out of the standard (a colour,
 * a typed line). A value that was saved is equal on both copies, so carrying
 * it over changes nothing. Lines bound to the speaker, before or after the
 * save, are left to the speaker refill. The Company line is appended last, so
 * every other control's path is the same on both copies.
 */
export function carriedOverEdits(
  oldElement: Record<string, unknown> | null | undefined,
  controls: ControlConfiguration[],
  previousControls: ControlConfiguration[],
): {
  placement: Record<string, unknown>;
  values: Array<{ elementPaths: number[][]; property: string; value: unknown }>;
} {
  const placement: Record<string, unknown> = {};
  if (!oldElement) return { placement, values: [] };
  for (const key of PLACEMENT_KEYS) {
    if (oldElement[key] !== undefined && oldElement[key] !== null)
      placement[key] = oldElement[key];
  }
  const bound = new Set(
    [speakerOf(controls), speakerOf(previousControls)].flatMap(sp =>
      Object.keys(sp?.controlBindings ?? {}),
    ),
  );
  const values: Array<{
    elementPaths: number[][];
    property: string;
    value: unknown;
  }> = [];
  for (const control of controls) {
    if (control.type === 'speaker' || !control.property) continue;
    if (control.id && bound.has(control.id)) continue;
    const path = control.elementPaths?.[0];
    if (!path) continue;
    const value = at(oldElement, path)?.[control.property];
    if (value === undefined) continue;
    values.push({
      elementPaths: control.elementPaths,
      property: control.property,
      value,
    });
  }
  return { placement, values };
}

/**
 * Turns a three-line nametag back into two: the Company line is unbound and
 * emptied, its element left where it is so no path moves, and the Position
 * line becomes the Title line again — the speaker's one line, typed or
 * derived in the Brand Kit's Speaker Format (PLAT-12179).
 */
export function removeCompanyLine<S extends SourceLike>(
  source: S | null | undefined,
  controls: ControlConfiguration[],
): CompanyLineResult<S> {
  const sp = speakerOf(controls);
  const entry = Object.entries(sp?.controlBindings ?? {}).find(
    ([, f]) => f === 'organization',
  );
  if (!sp || !entry || !source)
    return { ok: false, reason: 'It has no Company line.' };
  // The line to drop: the one that wasn't the Title before the toggle added
  // the third line (remembered then), else the Company line.
  const bindingsNow = sp.controlBindings ?? {};
  const keepId = sp.companyLine?.titleControlId;
  const positionId = Object.entries(bindingsNow).find(
    ([, f]) => f === 'role',
  )?.[0];
  const dropId =
    keepId && keepId === entry[0] && positionId ? positionId : entry[0];
  const titleId = dropId === entry[0] ? positionId : keepId;
  const companyControl = controls.find(c => c.id === dropId);
  const next: S = structuredClone(source);
  for (const path of companyControl?.elementPaths ?? []) {
    const el = at(next, path);
    if (el) el.text = '';
  }
  const nb: Record<string, SpeakerBindingKey> = {};
  for (const [id, f] of Object.entries(bindingsNow)) {
    if (id === dropId) continue;
    nb[id] = id === titleId ? 'title' : f;
  }
  const { companyLine: _forgotten, ...spRest } = sp;
  return {
    ok: true,
    source: next,
    controls: controls.map(c => {
      if (c === sp) return { ...spRest, controlBindings: nb };
      if (c === companyControl)
        return { ...c, enabled: false, default: { value: '' } };
      return c;
    }),
    note: 'The third line is emptied, and the Title shows again.',
  };
}
